import { create } from "zustand";
import { devtools } from "zustand/middleware";

export interface AdminUsersFilter {
  selectedOrganizationsFilter: string[];
  searchAdminUsersTerm: string;
  selectedPropertiesFilter: string[];
}

interface AdminUsersFiltersState {
  // State
  filters: AdminUsersFilter;

  // Actions
  setFilters: (newFilters: Partial<AdminUsersFilter>) => void;
  clearFilters: () => void;
  setSearchTerm: (term: string) => void;
  setOrganizationFilters: (organizations: string[]) => void;
  setPropertyFilters: (properties: string[]) => void;
}

const initialFilters: AdminUsersFilter = {
  selectedOrganizationsFilter: [],
  selectedPropertiesFilter: [],
  searchAdminUsersTerm: "",
};

// DevTools configuration options
const devToolsOptions = {
  // Name of the store in Redux DevTools (helps when you have multiple stores)
  name: "AdminUsersFilters",

  // Enable/disable DevTools based on environment (optional)
  // You could use process.env.NODE_ENV !== 'production' here
  enabled: true,

  // Custom anonymous action type (optional)
  anonymousActionType: "UNKNOWN_ACTION",

  // Serialize options (optional - used for handling complex objects)
  serialize: {
    // Options for handling circular references
    options: true,
  },
};

// State persists across React Router navigations (module singleton) and
// resets naturally on page refresh — no localStorage needed.
export const useAdminUsersFiltersStore = create<AdminUsersFiltersState>()(
  // DevTools should be the outermost middleware
  devtools(
    (set) => ({
      // Initial state
      filters: initialFilters,

      // Actions
      setFilters: (newFilters) =>
        // The third parameter to set allows us to name the action for DevTools
        set(
          (state) => ({
            filters: { ...state.filters, ...newFilters },
          }),
          false,
          {
            type: "admin-users-filters/setFilters",
            payload: newFilters,
          }
        ),

      clearFilters: () =>
        set(
          { filters: initialFilters },
          false,
          "admin-users-filters/clearFilters"
        ),

      setSearchTerm: (searchAdminUsersTerm) =>
        set(
          (state) => ({
            filters: { ...state.filters, searchAdminUsersTerm },
          }),
          false,
          {
            type: "admin-users-filters/setSearchTerm",
            payload: searchAdminUsersTerm,
          }
        ),

      setPropertyFilters: (selectedPropertiesFilter) =>
        set(
          (state) => ({
            filters: { ...state.filters, selectedPropertiesFilter },
          }),
          false,
          {
            type: "admin-users-filters/setPropertyFilters",
            payload: selectedPropertiesFilter,
          }
        ),

      setOrganizationFilters: (selectedOrganizationsFilter) =>
        set(
          (state) => ({
            filters: { ...state.filters, selectedOrganizationsFilter },
          }),
          false,
          {
            type: "admin-users-filters/setOrganizationFilters",
            payload: selectedOrganizationsFilter,
          }
        ),
    }),
    // Pass DevTools options as the second parameter to devtools
    devToolsOptions
  )
);
